import { useDropzone, } from 'react-dropzone';
import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { MdClose, MdCheck } from "react-icons/md";
import { FiUploadCloud } from "react-icons/fi";
import Resizer from 'react-image-file-resizer';
import ns from '@/helpers/NotificationService';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { showProgressSpinner } from '../../../helpers/ProgressSpinnerService';

interface Props {
    onImagesUpdated: (images: any[]) => void;
    acceptedFileExtensions: string;
    settings: any;
}

const BulkUploaderImageUploader: React.FC<Props> = props => {
    const {
        onImagesUpdated,
        acceptedFileExtensions,
        settings
    } = props;
    const [isBusy, setIsBusy] = useState(false);

    const imageValidator = (file: any): any => {
        if (!acceptedFileExtensions.includes(file.type)) {
            setIsBusy(false);
            return {
                code: "incorrect-format",
                message: "Supported file types are .JPEG | .JPG | .PNG"
            };
        }

        /* Added code */
        if (file.size > 10485760) {
            setIsBusy(false);
            ns.error(`Image ${file.name} is too big. A maximum image size of 10MB is allowed`);
            return {
                code: "size-too-large",
                message: "Size is larger than 10mb"
            };
        }

        return null;
    }

    const {
        acceptedFiles,
        fileRejections,
        isDragActive,
        isDragAccept,
        isDragReject, getRootProps, getInputProps
    } = useDropzone({
        accept: {
            'image/png': ['.png'],
            'text/html': ['.html', '.htm'],
            'image/jpeg': ['.jpeg', '.jpg']
        },
        multiple: true,
        validator: imageValidator
    });

    React.useEffect(() => {
        const process = async () => {
            if (acceptedFiles.length > 0) {
                showProgressSpinner({ description: "Preparing Images for Processing.\n\nThis can take several minutes.\nPlease do not close or refresh your browser." });

                const imageUrls: any[] = [];

                let batchNumber = 0;
                const batchSize = 10;

                let batch = acceptedFiles.slice(batchNumber * batchSize, (batchNumber + 1) * batchSize);

                while (batch.length > 0) {
                    showProgressSpinner({ description: `Preparing Images for Processing.\n\nThis can take several minutes.\nPlease do not close or refresh your browser.`, extra: `Compressing Image ${batchNumber * batchSize} of ${acceptedFiles?.length}...` });
                    const promises = batch.map(x => resizeFile(x));
                    var responses = await Promise.allSettled(promises);
                    responses.map((response: any) => imageUrls.push(response.value));
                    batchNumber++;
                    batch = acceptedFiles.slice(batchNumber * batchSize, (batchNumber + 1) * batchSize);
                }

                onImagesUpdated(imageUrls);
                setIsBusy(false);
            }
        }
        process();
    }, [acceptedFiles]);

    const resizeFile = (file: File) => new Promise(resolve => {
        if (!isBusy)
            Resizer.imageFileResizer(file, 1920, 1080, 'JPEG', 50, 0,
                base64 => {
                    let order = 0;
                    try {
                        order = Number(file.name.toUpperCase().replace(".JPG", "").replace(".JPEG", "").replace(".PNG", ""));
                    }
                    catch { }

                    const tempId = uuidv4();
                    resolve({
                        id: "",
                        fileName: file.name,
                        fileData: base64,
                        order: order,
                        _tempId: tempId
                    });
                }, 'base64', undefined, undefined
            );
    });

    useEffect(() => {
        if (fileRejections?.length === 1) ns.error(`Image ${fileRejections?.[0]?.file.name} is in the incorrect format. We only support .jpeg, .jpg and .png file types`);
        else if (fileRejections?.length > 1) ns.error(`Some images are in the incorrect format. We only support .jpeg, .jpg and .png file types`);
    }, [fileRejections])

    // Drop area like the other image uploaders, with the file naming rules as tips.
    return (
        <div {...getRootProps({ className: "pb-dropzone pb-dropzone--bulk" + (isDragAccept ? " is-accept" : "") + (isDragReject ? " is-reject" : "") })}>
            <input {...getInputProps()} />
            <div className="pb-dropzone-icon">
                {isDragAccept ? <MdCheck /> : isDragReject ? <MdClose /> : <FiUploadCloud />}
            </div>

            {isDragAccept && <div className="pb-dropzone-title">Drop to add</div>}
            {isDragReject && <div className="pb-dropzone-title text-danger">Incorrect file type. Only JPEG / JPG / PNG images no larger than 10mb are accepted.</div>}

            {!isDragActive && <>
                <div className="pb-dropzone-title">Drag and drop photos here</div>
                <div className="pb-dropzone-text">or browse your files. 16x9 images work best (e.g. 3840 x 2160, 2560 x 1440, 1920 x 1080, 1280 x 720); other sizes may be cut to scale.</div>
                <div className="pb-dropzone-chips">
                    <span>JPEG / JPG / PNG</span>
                    <span>Max 10MB per image</span>
                </div>
                <ul className="pb-banner-tips pb-bulk-naming">
                    <li>Name the banner / main image with a <strong>whole number</strong>, e.g. <strong>1.jpg</strong> for {settings.LotName?.toLowerCase()} 1</li>
                    <li>Name the rest with a <strong>single decimal place</strong>, e.g. <strong>1.1.jpg</strong>, <strong>1.2.jpg</strong></li>
                </ul>
                <Button disabled={isBusy} className="btn-primary text-white text-capitalize pb-dropzone-button">
                    {!isBusy ? "Browse Images" : "Processing..."}
                </Button>
            </>}

            {fileRejections?.length > 0 && <div className="pb-dropzone-rejected">
                <span>Images with incorrect format: </span>
                <span className="text-danger">{fileRejections.map(x => x.file.name).filter(x => x).join(", ")}</span>
            </div>}
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    settings: state.settings.settings,
})

const mapDispatchToProps = (dispatch: any) => {
    return {
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(BulkUploaderImageUploader);